import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { 
  Settings, ArrowLeft, Building2, Plus, Edit, Trash2, 
  Shield, CheckCircle, RefreshCw, MapPin, Hash, FileText,
  Users, XCircle, Eye, PlayCircle, PauseCircle, CreditCard,
  MoreHorizontal, MoreVertical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { financeApi } from '../../utils/finance-api';
import { toast } from 'sonner';
import { Company, DirectDebitUser, DirectDebit } from '../../types/direct-debit';
import { AbilityContext } from '../../context/casl/AbilityContext';
import AccessRestricted from '../../components/AccessRestricted';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { DeleteCompanyDialog } from '../../components/finance/DeleteCompanyDialog';

const FinanceSettingsPage = () => {
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const canReadSettings = ability.can(ACTIONS.READ, RESOURCES.DIRECT_DEBIT_FINANCE_COMPANY)
    || ability.can(ACTIONS.READ, RESOURCES.DIRECT_DEBIT);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "companies");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [authorizedUsers, setAuthorizedUsers] = useState<DirectDebitUser[]>([]);
  const [directDebits, setDirectDebits] = useState<DirectDebit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Company Form States
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyForm, setCompanyForm] = useState({
    id: '',
    name: '',
    code: '',
    abn: '',
    address: '',
    isActive: true
  });

  // Delete Dialog State
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  useEffect(() => {
    if (canReadSettings) loadData();
  }, [canReadSettings]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [companiesRes, debitsRes, usersRes] = await Promise.all([
        financeApi.listCompanies(),
        financeApi.listDirectDebits(),
        financeApi.listUsers()
      ]);
      setCompanies(companiesRes);
      setDirectDebits(debitsRes.data);
      setAuthorizedUsers(usersRes);
    } catch (error) {
      toast.error("Failed to load finance settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setCompanyForm({
      id: company.id,
      name: company.name,
      code: company.code || '',
      abn: company.abn || '',
      address: company.address || '',
      isActive: company.isActive
    });
    setShowCompanyForm(true);
  };

  const handleSaveCompany = async () => {
    if (!companyForm.name || !companyForm.code) {
      toast.error("Please fill in company name and code");
      return;
    }
    try {
      // Sanitize data: convert empty strings to null for backend compatibility
      const sanitizedData = Object.entries(companyForm).reduce((acc, [key, value]) => {
        acc[key] = value === '' ? null : value;
        return acc;
      }, {} as any);

      if (editingCompany) {
        await financeApi.updateCompany(editingCompany.id, sanitizedData);
        toast.success(`Company "${companyForm.name}" updated successfully`);
      } else {
        // Omit the empty id field (already nullified by sanitizer) to allow database to generate a new UUID
        const { id, ...createData } = sanitizedData;
        await financeApi.createCompany(createData);
        toast.success(`Company "${companyForm.name}" added successfully`);
      }
      setShowCompanyForm(false);
      setEditingCompany(null);
      setCompanyForm({ id: '', name: '', code: '', abn: '', address: '', isActive: true });
      loadData();
    } catch (error: any) {
      toast.error(error.message || "Failed to save company");
    }
  };

  const handleToggleUserAccess = async (userId: string) => {
    const user = authorizedUsers.find(u => u.id === userId);
    if (!user) return;

    try {
      const newHasAccess = !user.hasAccess;
      await financeApi.updateUserAccess(userId, { 
        hasAccess: newHasAccess, 
        accessLevel: user.accessLevel 
      });
      
      setAuthorizedUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, hasAccess: newHasAccess } : u
      ));
      
      toast.success(`${user.name} ${newHasAccess ? 'granted' : 'revoked'} Direct Debit Register access`);
    } catch (error: any) {
      toast.error(error.message || "Failed to update user access");
    }
  };

  const handleChangeUserAccessLevel = async (userId: string, level: DirectDebitUser['accessLevel']) => {
    const user = authorizedUsers.find(u => u.id === userId);
    if (!user) return;

    try {
      await financeApi.updateUserAccess(userId, { 
        hasAccess: user.hasAccess, 
        accessLevel: level 
      });
      
      setAuthorizedUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, accessLevel: level } : u
      ));
      
      toast.success(`${user.name}'s access level changed to ${level}`);
    } catch (error: any) {
      toast.error(error.message || "Failed to update access level");
    }
  };

  const handleToggleCompanyStatus = async (company: Company) => {
    try {
      const newStatus = !company.isActive;
      await financeApi.updateCompany(company.id, { isActive: newStatus });
      
      setCompanies(prev => prev.map(c => 
        c.id === company.id ? { ...c, isActive: newStatus } : c
      ));
      
      toast.success(`Company "${company.name}" ${newStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteCompany = (company: Company) => {
    const isUsed = directDebits.some(d => d.companyId === company.id);
    if (isUsed) {
      toast.error("Cannot delete company as it is used in existing debits");
      return;
    }
    setCompanyToDelete(company);
    setShowDeleteDialog(true);
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return;

    try {
      await financeApi.deleteCompany(companyToDelete.id);
      toast.success("Company deleted successfully");
      setShowDeleteDialog(false);
      setCompanyToDelete(null);
      loadData();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete company");
    }
  };

  const formatCurrency = (amount: number) => {
    const safeAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(safeAmount);
  };

  if (!canReadSettings) {
    return (
      <AccessRestricted
        navigateTo="/finance/direct-debit"
        backMessage="Back to Direct Debit Register"
        accessMessage="You don't have permission to view direct debit settings."
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-10 w-10 border border-slate-100 hover:bg-white hover:shadow-sm" 
          onClick={() => navigate('/finance/direct-debit')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-slate-400" />
          <h1 className="text-2xl font-bold text-slate-900">Direct Debit Register Settings</h1>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* <TabsList className="bg-slate-100 p-1 inline-flex h-10 items-center justify-center rounded-full text-slate-500 mb-6">
          <TabsTrigger 
            value="users" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            User Access
          </TabsTrigger>
          <TabsTrigger 
            value="companies" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            Companies
          </TabsTrigger>
        </TabsList> */}

        {/* User Access Management */}
        <TabsContent value="users" className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">User Access Management</h3>
                <p className="text-sm text-muted-foreground">Control which users can access the Direct Debit Register and their permission levels</p>
              </div>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {authorizedUsers.filter(u => u.hasAccess).length} Active Users
              </Badge>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role & Department</TableHead>
                      <TableHead>Access Level</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-32 text-center">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-200" />
                        </TableCell>
                      </TableRow>
                    ) : authorizedUsers.length > 0 ? (
                      authorizedUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={user.avatar} alt={user.name} />
                                <AvatarFallback>{user.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{user.role}</p>
                              <p className="text-sm text-muted-foreground">{user.department}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={user.accessLevel} 
                              disabled={!user.hasAccess}
                              onValueChange={(val: any) => handleChangeUserAccessLevel(user.id, val)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="view">View Only</SelectItem>
                                <SelectItem value="edit">Edit Access</SelectItem>
                                <SelectItem value="admin">Admin Access</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {user.hasAccess ? (
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" /> Active
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">
                                <XCircle className="h-3 w-3 mr-1" /> No Access
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant={user.hasAccess ? "outline" : "default"} 
                              size="sm" 
                              onClick={() => handleToggleUserAccess(user.id)}
                            >
                              {user.hasAccess ? (
                                <>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Remove Access
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Grant Access
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="h-64 text-center py-20 text-slate-400 italic">
                          <div className="flex flex-col items-center justify-center opacity-50">
                            <Users className="h-12 w-12 mb-3" />
                            <p>No users found matching your selection.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Access Level Definitions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-4 w-4 text-gray-600" />
                      <span className="font-medium">View Only</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Can view direct debits and reports but cannot create or modify records</p>
                  </div>
                  <div className="p-4 border rounded-lg bg-blue-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Edit className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Edit Access</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Can create, edit, and manage direct debits but cannot access settings</p>
                  </div>
                  <div className="p-4 border rounded-lg bg-red-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-4 w-4 text-red-600" />
                      <span className="font-medium">Admin Access</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Full access including settings, user management, and system configuration</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Company Management */}
        <TabsContent value="companies" className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Company Management</h3>
                <p className="text-sm text-muted-foreground">Manage companies available for direct debit selection</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden md:flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {companies.filter(c => c.isActive).length} Active
                  </Badge>
                  <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                    {companies.filter(c => !c.isActive).length} Inactive
                  </Badge>
                  <Button onClick={() => setShowCompanyForm(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add Company
                  </Button>
                </div>

                {/* Mobile Header Actions */}
                <div className="md:hidden">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-10 w-10">
                        <MoreHorizontal className="h-5 w-5 text-slate-600" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 p-2 space-y-1">
                      <DropdownMenuItem onClick={() => setShowCompanyForm(true)} className="rounded-lg h-11 font-bold gap-3">
                        <Plus className="h-4 w-4 text-blue-600" />
                        Add New Company
                      </DropdownMenuItem>
                      <div className="h-px bg-slate-100 my-1" />
                      <div className="px-2 py-2 space-y-2">
                         <div className="flex items-center justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                           <span>Stats</span>
                         </div>
                         <div className="flex gap-2">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] h-5">
                              {companies.filter(c => c.isActive).length} Active
                            </Badge>
                            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-[10px] h-5">
                              {companies.filter(c => !c.isActive).length} Inactive
                            </Badge>
                         </div>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            {showCompanyForm && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {editingCompany ? 'Edit Company' : 'Add New Company'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        placeholder="e.g., IdaratPro Pty Ltd"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Company Code *</Label>
                      <Input
                        placeholder="e.g., IPL"
                        value={companyForm.code}
                        onChange={(e) => setCompanyForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>ABN</Label>
                      <Input
                        placeholder="e.g., 12 345 678 901"
                        value={companyForm.abn}
                        onChange={(e) => setCompanyForm(prev => ({ ...prev, abn: e.target.value }))}
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-6">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={companyForm.isActive}
                        onChange={(e) => setCompanyForm(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="rounded"
                      />
                      <Label htmlFor="isActive">Active Company</Label>
                    </div>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Textarea
                      placeholder="Company address..."
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, address: e.target.value }))}
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowCompanyForm(false);
                        setEditingCompany(null);
                        setCompanyForm({ id: '', name: '', code: '', abn: '', address: '', isActive: true });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveCompany}>
                      {editingCompany ? 'Update Company' : 'Add Company'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Code & ABN</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Usage</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow><TableCell colSpan={6} className="h-32 text-center"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-200" /></TableCell></TableRow>
                    ) : companies.length > 0 ? (
                      companies.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-blue-600" />
                              <div>
                                <p className="font-medium">{company.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  Created {new Date(company.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{company.code}</p>
                              <p className="text-sm text-muted-foreground">{company.abn || 'No ABN'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{company.address || 'No address'}</p>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={company.isActive ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'}
                            >
                              {company.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {/* 
                                Usage Implementation:
                                This section calculates the real-world usage of each Finance Company by filtering the
                                global directDebits array. It checks both the 'companyId' field and the nested 
                                'company.id' object for maximum reliability across different API response formats.
                                - 'debits': Total number of active/inactive debit agreements linked to this entity.
                                - 'value': The aggregate monthly liability amount committed to this company.
                            */}
                            <div className="text-sm">
                              <p className="font-medium text-slate-900">
                                {directDebits.filter(d => d.companyId === company.id || (typeof d.company === 'object' && d.company?.id === company.id)).length} debits
                              </p>
                              <p className="text-muted-foreground font-medium">
                                {formatCurrency(directDebits.filter(d => d.companyId === company.id || (typeof d.company === 'object' && d.company?.id === company.id)).reduce((sum, d) => sum + Number(d.monthlyDebitAmount), 0))}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="hidden md:flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditCompany(company)}><Edit className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleToggleCompanyStatus(company)}>
                                {company.isActive ? <PauseCircle className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCompany(company)} disabled={directDebits.some(d => d.companyId === company.id || (typeof d.company === 'object' && d.company?.id === company.id))}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            {/* Mobile Row Actions */}
                            <div className="md:hidden">
                               <DropdownMenu>
                                 <DropdownMenuTrigger asChild>
                                   <Button variant="ghost" size="icon" className="h-8 w-8">
                                     <MoreHorizontal className="h-4 w-4" />
                                   </Button>
                                 </DropdownMenuTrigger>
                                 <DropdownMenuContent align="end" className="w-48 p-2 space-y-1">
                                   <DropdownMenuItem onClick={() => handleEditCompany(company)} className="rounded-lg font-bold gap-3">
                                      <Edit className="h-4 w-4 text-blue-600" />
                                      Edit Details
                                   </DropdownMenuItem>
                                   <DropdownMenuItem onClick={() => handleToggleCompanyStatus(company)} className="rounded-lg font-bold gap-3">
                                      {company.isActive ? (
                                        <>
                                          <PauseCircle className="h-4 w-4 text-amber-600" />
                                          Deactivate
                                        </>
                                      ) : (
                                        <>
                                          <PlayCircle className="h-4 w-4 text-green-600" />
                                          Activate
                                        </>
                                      )}
                                   </DropdownMenuItem>
                                   <div className="h-px bg-slate-100 my-1" />
                                   <DropdownMenuItem 
                                      onClick={() => handleDeleteCompany(company)} 
                                      disabled={directDebits.some(d => d.companyId === company.id || (typeof d.company === 'object' && d.company?.id === company.id))}
                                      className="rounded-lg font-bold gap-3 text-red-600 focus:text-red-700"
                                   >
                                      <Trash2 className="h-4 w-4" />
                                      Delete Company
                                   </DropdownMenuItem>
                                 </DropdownMenuContent>
                               </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-64 text-center py-20 text-slate-400 italic">
                          <div className="flex flex-col items-center justify-center opacity-50">
                            <Building2 className="h-12 w-12 mb-3" />
                            <p>No companies found matching your selection.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <DeleteCompanyDialog 
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={confirmDeleteCompany}
        companyName={companyToDelete?.name}
      />
    </div>
  );
};

export default FinanceSettingsPage;
